import { useEffect, useState } from 'react';
import OverlayLoader from '../../../../../components/overlayLoader';
import { Box, Container, Grid2, MenuItem } from '@mui/material';
import LoanCard from '../components/LoanCard';
import { resellerStatusPerLoanType } from '../utils';
import { Typography, TextField } from '@mui/material';
import { useTranslation } from 'react-i18next';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useAuth } from '../../../../../services/auth/AuthContext';

import fetchApplicationsService from '../../../../../services/api/fetchApplications';
import Navbar from '../../../../../components/shared-menu/Navbar';

export default function SearchCustomer() {
	const { t } = useTranslation();

	const { setIsAuthenticated } = useAuth();
	const navigate = useNavigate();

	const [searchParams, setSearchParams] = useSearchParams();
	const searchType = searchParams.get('search_by') ?? 'phone';
	const searchTerm = searchParams.get('search_term') ?? '';
	const setParam = (key, value) => setSearchParams((searchParams) => {
		searchParams.set(key, value);
		return searchParams;
	});
	const setSearchType = (searchType) => {
		setParam('search_by', searchType);
		setParam('search_term', '');
		setApplications([]);
	};
	const setSearchTerm = (searchTerm) => setParam('search_term', searchTerm);

	const [applications, setApplications] = useState([]);
	const [resellerData, setResellerData] = useState({});
	const [loading, setLoading] = useState(false);
	const [error, setError] = useState(null);
	const [hasSearched, setHasSearched] = useState(false); // Add this new state variable

	// Add this helper function at the top of your component
	const parseSearchTerms = (searchTerm) => {
		return searchTerm.split(',').map(term => term.trim()).filter(term => term.length > 0);
	};

	const handleSearch = async () => {
		setLoading(true);
		setError(null);

		const searchTerms = parseSearchTerms(searchTerm);
		if (searchTerms.length === 0) {
			setHasSearched(false);
			setError(null);
			setLoading(false);
			setApplications([]);
			return;
		}
		
		setHasSearched(true);
	
		try {
			const response = await fetchApplicationsService.getApplicationsForStatusCode(
				null,
				{ [searchType]: searchTerms.join(',') },
				setIsAuthenticated,
				navigate
						);

			// Extract all unique identifiers from the response based on search type
			let matchedTerms = [];
						if (response.applications && response.applications.length > 0) {
				setApplications(response.applications);
				setResellerData((prev) => ({
					...prev,
					...response.reseller_data,
				}) || {});
				
				// Use the responses to determine which terms matched
				if (searchType === 'phone') {
					matchedTerms = response.applications.map(app => app.enquiry_details.whatsapp_number);
				} else if (searchType === 'enquiry_id') {
					matchedTerms = response.applications.map(app => app.enquiry_details.id);
						}
				
				// If backend provides matched_terms, use that instead
				if (response.matched_terms && Array.isArray(response.matched_terms)) {
					matchedTerms = response.matched_terms;
					}
				
				// Get unique terms
				matchedTerms = [...new Set(matchedTerms)];
				
				// Find which search terms weren't matched
				const unmatchedTerms = searchTerms.filter(term => 
					!matchedTerms.some(match => match.toString().includes(term))
				);
				
				if (unmatchedTerms.length > 0) {
					setError(`Found results, but no matches for: ${unmatchedTerms.join(', ')}`);
				}
			} else {
				setApplications([]);
				setError(`No matches found for: ${searchTerms.join(', ')}`);
			}
		} catch (err) {
			setError('dashboard.search_customers.errors.fetch_failed');
			setApplications([]);
		} finally {
			setLoading(false);
		}
	};

	// Update the useEffect debounce logic
	useEffect(() => {
		const searchTerms = parseSearchTerms(searchTerm);

		if (searchTerms.length === 0) {
			setHasSearched(false);
			setError(null); // Clear error message when search terms are removed
			setApplications([]); 
			return;
		}

		// For phone search, check minimum length requirement on any individual term
		if (searchType === 'phone' &&
			searchTerms.some(term => term.length < 2)) {
			return;
		}

		const delayDebounce = setTimeout(() => {
			handleSearch();
		}, 500); // Debounce to avoid excessive API calls
		return () => clearTimeout(delayDebounce);
	}, [searchTerm, searchType]);

    const addNewChatRecord = (chat, index) => {
        setApplications((prev) => {
            if (prev[index].active_chats.find((c) => c.id === chat.id)) {
                return prev;
            }

            const newApplications = [...prev];
            newApplications[index].active_chats = [
                chat,
                ...newApplications[index].active_chats,
            ];
            return newApplications;
        });
    };

    const rejectApplication = (enquiryId) => {
		const application = applications.find((app) => app.enquiry_details.id === enquiryId);
		const rejectionCode = Object.keys(
			resellerStatusPerLoanType[application.loan_type.code].rejected_loans
		)[0];

		setApplications((prev) => prev.map((app) => {
			if (app.enquiry_details.id === enquiryId) {
				app.current_status_code = rejectionCode;
				app.enquiry_details.application_submission_status = 'rejected';
			}

			return app;
		}));
    };

	return (
   <>
  			<Navbar />
  			<OverlayLoader open={loading} text={t('loading.submitted') + '...'} />
  			<Container maxWidth="lg">
  				<Typography variant="h4" textAlign="center" my={2}>
  					{t('dashboard.pages.search_customer')}
  				</Typography>

  				<Box display="flex" gap={2} mb={2}>
  					<TextField
  						select
  						value={searchType}
  						onChange={(e) => setSearchType(e.target.value)}
  						label={t('dashboard.search_customers.search_by.label')}
  						variant="outlined"
  						sx={{ minWidth: 150 }}
  					>
  						<MenuItem value="phone">
  							{t('dashboard.search_customers.search_by.phone')}
  						</MenuItem>
  						<MenuItem value="enquiry_id">
  							{t('dashboard.search_customers.search_by.enquiry_id')}
  						</MenuItem>
  						{/* <MenuItem value="loanId">
  							{t('dashboard.search_customers.search_by.loan_id')}
  						</MenuItem>
  						<MenuItem value="name">
  							{t('dashboard.search_customers.search_by.name')}
  						</MenuItem>
  						<MenuItem value="customerId">
  							{t('dashboard.search_customers.search_by.customer_id')}
  						</MenuItem> */}
  					</TextField>

  					<TextField
  						value={searchTerm}
  						onChange={(e) => setSearchTerm(e.target.value)}
  						placeholder={t('dashboard.search_customers.search_by.placeholder') + " (comma-separated values supported)"}
  						variant="outlined"
  						fullWidth
  					/>
  				</Box>

  				{error && (
  					<Typography color="error" variant="body2" gutterBottom>
  						{error.startsWith('No matches found for:')
  							? error
  							: t(error)}
  					</Typography>
  				)}

  				{
  					applications.length > 0 ? (
  						applications.map((application, index) => (
  							<Grid2 key={application.id || index} xs={12} mb={2}>
  								<LoanCard
  									title={
  										application.enquiry_details.business_name
  											? `${application.enquiry_details.business_name} (${application.enquiry_details.whatsapp_number})`
  											: application.enquiry_details.whatsapp_number
  									}
  									application={application}
  									allStatus={
  										application.loan_type?.code
  											? resellerStatusPerLoanType[application.loan_type.code].all(application.current_status_code)
  											: resellerStatusPerLoanType["null"]?.in_progress
  									}
  									currentStatusCode={application.current_status_code}
  									resellerData={resellerData}
  									addNewChatRecord={(chat) => addNewChatRecord(chat, index)}
  									rejectApplication={rejectApplication}
  									loanType={application.loan_type?.code}
  								/>
  							</Grid2>
  						))
  					) : (
  						!loading &&
  						hasSearched && (
  							<Typography variant="body2" color="textSecondary">
  								{t('dashboard.search_customers.no_results')}
  							</Typography>
  						)
  					)
  				}
  			</Container>
	
   </>
 );
}
